import React, { FC, memo, useCallback, useContext, useMemo } from 'react'
import { ThemeContext } from 'styled-components'
import { useTranslation } from '../../../../hooks/locale'
import { ListingSectionObject } from '../../../../transports/common/listing-section-transport'
import { landingPageEventCategories } from '../../../../services/analytics-service/categories'
import { landingPageEventActions } from '../../../../services/analytics-service/actions'
import AnalyticsService from '../../../../services/analytics-service'
import { getPropertyClickRedirectUrl } from '../../utils'
import { usePropertyCardTracking } from '../../hooks'
import { encodeToBase64 } from '../../../../utils/encoding-utils'
import { isServer } from '../../../../utils/platform-utils'
import get from 'lodash/get'
import {
    GridCardContainer,
    ImageContainer,
    CardContent,
    LocationText,
    RatingContainer,
    RatingText,
    PropertyTitle,
    PropertyDescription,
    PriceContainer,
    FavoriteButton,
    FullStarIcon,
    LocationRatingContainer,
    LocationContainer,
    PersonDetails,
    OfferBadge,
    OfferIcon,
    OfferText,
    ObjectTopRecommendationPill,
} from './styles'
import NextImage from '../../../common/next-image'

import Star from '../../../../assets/icons/star.svg'
import WishlistSection from '../../object-cards/wishlist-section'
import type { BreadcrumbListTransport } from '../../../../models/common/breadcrumb-model'
import { Offer } from '../../../../models/property-details-page/offers-model'
import { useDateFormatter } from '../../../../hooks/date'
import { SeoHref } from '../../../landing-page/common/property-card/vertical-property-card-styles'

interface GridCardProps {
    listingObject: ListingSectionObject
    position: number
    breadcrumbList?: BreadcrumbListTransport
    requestUrl?: string
}

const GridCard: FC<GridCardProps> = ({ listingObject, requestUrl, breadcrumbList }) => {
    const { t } = useTranslation()
    const { format } = useDateFormatter()
    const theme = useContext(ThemeContext)

    const seoRegion = get(listingObject, 'seoRegion', {})
    const rating = get(listingObject, 'rating', null)
    const listingId = get(listingObject, 'id')
    const breadcrumbs = get(listingObject, 'breadcrumbs', [])
    const stats = get(listingObject, 'stats', {})
    const isPremiumTopObject = get(listingObject, 'isPremiumTopObject', false)
    const isTopObject = get(listingObject, 'isTopObject', false)
    const images = get(listingObject, 'images', [])
    const href = get(listingObject, 'href', '')
    const offer = get(listingObject, 'offer', null)
    const price = get(listingObject, 'price', {})

    const propertyTitle = useMemo(() => {
        const propertyType = get(listingObject, 'propertyType', '')
        const title = get(listingObject, 'title', '')
        return `${propertyType} ${title}`.trim()
    }, [listingObject])

    const offerFormatter = useMemo(() => new Offer(t, format), [t, format])

    const formattedOffer = useMemo(() => {
        return offer ? offerFormatter.format(offer) : null
    }, [offer, offerFormatter])

    const formattedBreadcrumb = useMemo(() => {
        return breadcrumbList?.getLastRegionLink(breadcrumbs, '') || null
    }, [breadcrumbList, breadcrumbs])

    // TODO (Future): Check this pass listingType
    const { propertyCardClickTracking, intersectionTargetRef } = usePropertyCardTracking(listingObject)

    const handleCardClick = useCallback(() => {
        // TODO (Future): Will add sentry here for debugging object issue
        const safeHref = typeof href === 'string' ? href : ''

        const eventCategory = `${landingPageEventCategories.listingCardSection.TOP_INSERAT} - ${safeHref}`
        const eventAction = landingPageEventActions.listingCardSection.TOP_INSERAT
        const _objectURL = getPropertyClickRedirectUrl(t('traumBaseUrl'), safeHref)

        // handle traumBoost tracking
        if (isPremiumTopObject || isTopObject) {
            // handle traumBoost tracking
            // from_page & tb will only be set for all top listings in RLP
            // from_page contains base64 encoded url of the page from which the request is made
            _objectURL.searchParams.set(
                'from_page',
                isServer ? encodeToBase64(requestUrl ?? '') : encodeToBase64(window.location.href),
            )
            _objectURL.searchParams.set('tb', '1')
        }

        propertyCardClickTracking(listingObject, null)

        AnalyticsService.trackEvent({ action: eventAction, category: eventCategory, label: _objectURL.toString() })
        window.open(_objectURL.href, '_blank')
    }, [href, t, listingObject, propertyCardClickTracking])

    const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
    }, [])

    const regionName = get(formattedBreadcrumb, 'title') || get(seoRegion, 'name', '')
    const regionLink = get(formattedBreadcrumb, 'url') || get(seoRegion, 'url', '')

    const propertyImage = get(images, '[0]', null)

    // Build property description
    const propertyDescriptionText = useMemo(() => {
        const bedroomsCount = get(stats, 'bedrooms', null)
        const maxPersonsCount = get(stats, 'maxPersons', null)

        const bedroomsStat = bedroomsCount ? t('properBedRoomCount', { count: bedroomsCount }) : null
        const maxPersonStat = maxPersonsCount ? t('maxGuestCount', { count: maxPersonsCount }) : null

        if (bedroomsStat && maxPersonStat) {
            return `${bedroomsStat}, ${maxPersonStat}`
        }
        return bedroomsStat || maxPersonStat || ''
    }, [stats, t])

    // Build price info
    const priceInfo = useMemo(() => {
        const persons = get(price, 'persons')
        const unit = get(price, 'unit')
        return persons && unit ? `${persons} / ${unit}` : null
    }, [price])

    // Check if rating should be displayed
    const shouldShowRating = useMemo(() => {
        return (
            rating &&
            get(rating, 'count', 0) > 0 &&
            get(rating, 'average') !== undefined &&
            get(rating, 'average') !== null
        )
    }, [rating])

    const handleRegionClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation()
    }, [])

    if (!listingObject) {
        return null
    }

    const displayPrice = get(price, 'price', '')

    return (
        <GridCardContainer ref={intersectionTargetRef} onClick={handleCardClick}>
            <ImageContainer>
                {propertyImage && (
                    <NextImage src={get(propertyImage, 'url', '')} alt={get(propertyImage, 'alt', 'Property image')} />
                )}
                {isTopObject && <ObjectTopRecommendationPill>{t('topListing')}</ObjectTopRecommendationPill>}
                <FavoriteButton onClick={handleFavoriteClick}>
                    <WishlistSection
                        containerBackgroundColor={theme.colors.white}
                        containerBorderRadius="1.456rem"
                        containerHeight="1.725rem"
                        containerWidth="1.725rem"
                        containerMargin="0"
                        containerPadding="0.25rem"
                        width="0.863rem"
                        height="0.863rem"
                        listingId={listingId?.toString() || ''}
                    />
                </FavoriteButton>
                {offer && formattedOffer && (
                    <OfferBadge>
                        <OfferIcon />
                        <OfferText>
                            {get(formattedOffer, 'discount', '')} ● {get(formattedOffer, 'label', '')}
                        </OfferText>
                    </OfferBadge>
                )}
            </ImageContainer>

            <CardContent>
                <LocationRatingContainer>
                    <LocationContainer>
                        <SeoHref title={regionName} href={regionLink} onClick={handleRegionClick}>
                            <LocationText>{regionName}</LocationText>
                        </SeoHref>
                    </LocationContainer>
                    {shouldShowRating && (
                        <RatingContainer>
                            <FullStarIcon as={Star} />
                            <RatingText>{Number(get(rating, 'average', 0)).toFixed(1)}</RatingText>
                        </RatingContainer>
                    )}
                </LocationRatingContainer>

                <PropertyTitle>{propertyTitle}</PropertyTitle>

                {propertyDescriptionText && <PropertyDescription>{propertyDescriptionText}</PropertyDescription>}

                {displayPrice && <PriceContainer>{displayPrice}</PriceContainer>}

                {priceInfo && <PersonDetails>{priceInfo}</PersonDetails>}

                {/* TODO (Future): Feature data will be provided in a new format from the
                    backend. Will show the data once the new structure is available.  */}
            </CardContent>
        </GridCardContainer>
    )
}

export default memo(GridCard)
